import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import {
  CheckCircle2,
  Sparkles,
  PackageCheck,
  Repeat,
  Building2,
  Factory,
  HardHat,
  Dumbbell,
  Wrench,
  Hospital,
  Phone,
  Hotel,
  Truck,
  Store,
  GraduationCap,
} from "lucide-react";
import SiteVisitForm from "@/components/SiteVisitForm";
import VideoBackground from "@/components/VideoBackground";

const HERO_IMG =
  "/media/vending-machine-orange-wall.png";

export default function About() {
  return (
    <div data-testid="about-page" className="text-white">
      <Seo />
      <HeroSection />
      <OurStorySection />
      <WhatWeStandForSection />
      <WhereWeInstallSection />
      <FormSection />
    </div>
  );
}

function HeroSection() {
  return (
    <section className="relative overflow-hidden border-b border-white/5">
      <VideoBackground testId="about-hero-video" />
      <div className="relative mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center rounded-full border border-[#FE7200]/30 bg-[#FE7200]/10 px-3 py-1 text-xs font-semibold tracking-wide text-[#FE7200]">
            Anytime Vending Machines
          </div>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            About Us
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-2xl leading-relaxed">
            We make it ridiculously easy for qualified workplaces to get a <b className="text-white">modern vending machine installed free</b> — fully stocked, fully serviced, and monitored so it stays reliable.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="#book-form"
              data-testid="about-book-button"
              className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
            >
              Book A Free Site Visit
            </a>
            <Link
              to="/"
              data-testid="about-home-button"
              className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-white/10 transition-colors"
            >
              Back To Home
            </Link>
          </div>
          <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-300">
            {["No lock-in contracts", "Tap & Go + cash/coin", "Stocked & serviced by us"].map((b) => (
              <li key={b} className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#FE7200]" /> {b}
              </li>
            ))}
          </ul>

          <div className="mt-10 relative rounded-2xl overflow-hidden border border-white/10 bg-[#121212]">
            <img src={HERO_IMG} alt="Modern vending machine" className="w-full h-80 sm:h-96 object-cover opacity-95" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 text-sm sm:text-base">
              <span className="font-semibold text-white">Modern machines</span>
              <span className="text-zinc-300"> · Tap &amp; Go · Fully stocked &amp; serviced by us.</span>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7">
            <h3 className="font-display text-xl font-bold text-white">Why we exist</h3>
            <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
              Too many machines are old, unreliable, and rarely serviced. We built a better standard: modern machines, modern payments, and proactive restocking so your staff actually use it.
            </p>
            <ul className="mt-5 space-y-3 text-sm">
              {[
                [<b className="text-white">Free install</b>, " for 30+ staff or steady foot traffic"],
                [<b className="text-white">Custom stock</b>, " — snacks, drinks, meals & healthy options"],
                [<b className="text-white">Ongoing service</b>, " — restocking, cleaning & maintenance"],
                [<b className="text-white">Back-to-base monitoring</b>, " — stock levels tracked automatically"],
              ].map((row, i) => (
                <li key={i} className="flex items-start gap-3 text-zinc-300">
                  <CheckCircle2 className="h-4 w-4 text-[#FE7200] mt-0.5" />
                  <div>{row[0]}{row[1]}</div>
                </li>
              ))}
            </ul>
            <a
              href="#book-form"
              data-testid="about-sidebar-book"
              className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[#FE7200] px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] text-black hover:bg-[#FF8C2E] transition-colors"
            >
              Book A Free Site Visit
            </a>
            <p className="mt-3 text-xs text-zinc-500 text-center">
              Best fit: <b className="text-zinc-300">30–80+</b> staff. Smaller sites may still qualify based on foot traffic.
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}

function OurStorySection() {
  const cards = [
    { icon: Sparkles, title: "Modern machines", body: "Clean, professional machines that people actually want to use — with Tap & Go and cash/coin support." },
    { icon: PackageCheck, title: "Zero hassle", body: "You don't buy the machine and you don't manage it. We handle delivery, install, restocks and maintenance." },
    { icon: Repeat, title: "Better stocking", body: "Choose what goes inside — snacks, drinks, meals, healthy options, or \u201cno junk food\u201d setups." },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-24 border-t border-white/5" data-testid="our-story-section">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Our story</h2>
      <p className="mt-3 text-zinc-300 max-w-3xl">
        We started Anytime Vending Machines because most vending felt stuck in the past: old machines, cash-only payments, inconsistent stock, and slow service. Workplaces deserved better.
      </p>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
        {cards.map((c, i) => (
          <div
            key={c.title}
            data-testid={`about-story-card-${i}`}
            className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7 hover:border-[#FE7200]/40 hover:-translate-y-1 transition-all"
          >
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#FE7200]/15 border border-[#FE7200]/30 text-[#FE7200]">
              <c.icon className="h-5 w-5" />
            </div>
            <h3 className="mt-4 font-display text-xl font-bold text-white">{c.title}</h3>
            <p className="mt-2 text-sm text-zinc-300 leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="about-risk-free-row">
        <div>
          <h3 className="font-display text-xl font-bold text-white">Risk-free trial</h3>
          <p className="mt-1 text-sm text-zinc-300">If it's not being used, we'll collect it. No lock-ins.</p>
        </div>
        <a
          href="#book-form"
          data-testid="about-risk-free-cta"
          className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-xs font-bold uppercase tracking-[0.18em] text-black hover:bg-[#FF8C2E] transition-colors"
        >
          Book A Free Site Visit
        </a>
      </div>
    </section>
  );
}

function WhatWeStandForSection() {
  const values = [
    { n: 1, title: "Reliability", text: "Machines should work, be stocked, and be clean — every day." },
    { n: 2, title: "Speed", text: "When stock runs low or a fault happens, we respond fast." },
    { n: 3, title: "Choice", text: "Workplaces choose what goes inside — not a one-supplier limitation." },
    { n: 4, title: "Zero pressure", text: "Try it risk-free. If it's not a fit, we remove it — simple." },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-24 border-t border-white/5" data-testid="stand-for-section">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">What we stand for</h2>
      <p className="mt-3 text-zinc-300">Simple values that keep service consistent.</p>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        {values.map((v, i) => (
          <div
            key={v.n}
            data-testid={`about-value-${i}`}
            className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7 flex gap-5"
          >
            <div className="shrink-0 h-10 w-10 rounded-full bg-[#FE7200] text-black font-display font-extrabold flex items-center justify-center text-lg">
              {v.n}
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-white">{v.title}</h3>
              <p className="text-sm text-zinc-400 mt-1">{v.text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function WhereWeInstallSection() {
  const tags = [
    { label: "Warehouses", icon: Building2 },
    { label: "Factories", icon: Factory },
    { label: "Construction Sites", icon: HardHat },
    { label: "Gyms", icon: Dumbbell },
    { label: "Workshops", icon: Wrench },
    { label: "Offices", icon: Building2 },
    { label: "Hospitals & Clinics", icon: Hospital },
    { label: "Call Centres", icon: Phone },
    { label: "Hotels", icon: Hotel },
    { label: "Transport Depots", icon: Truck },
    { label: "Large Retail", icon: Store },
    { label: "Schools & Universities", icon: GraduationCap },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-24 border-t border-white/5" data-testid="where-we-install-section">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Where we install</h2>
      <p className="mt-3 text-zinc-300">
        We install across the country for workplaces and facilities with <b className="text-white">30+ staff</b> or steady foot traffic.
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        {tags.map((t, i) => (
          <span
            key={t.label}
            data-testid={`about-install-chip-${i}`}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#121212] px-4 py-2 text-sm text-zinc-200 hover:border-[#FE7200]/50 transition-colors"
          >
            <t.icon className="h-4 w-4 text-[#FE7200]" /> {t.label}
          </span>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-[#FE7200]/40 bg-[#FE7200]/10 p-5 text-sm text-[#FFB870]" data-testid="about-not-sure-callout">
        Not sure if you qualify? Submit the form below and we'll confirm staff numbers, access, and placement.
      </div>
    </section>
  );
}

function FormSection() {
  return (
    <section id="book-form" className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 py-10 md:py-16 border-t border-white/5" data-testid="about-form-section">
      <SiteVisitForm source="about-page" testIdPrefix="about-booking" />
    </section>
  );
}
